import React, { useEffect, useState } from "react";
import { api, formatErr, fmtMoney } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CURRENCY_CATALOG, currencyName } from "@/lib/currencies";
import { ArrowLeftRight, Save, Trash2, RefreshCw, TrendingUp, Coins, RotateCcw, Wallet, Landmark, FileText, Search, MessageCircle, Settings } from "lucide-react";

const CUR = CURRENCY_CATALOG.map((c) => c.code);
const round6 = (n) => Math.round(n * 1e6) / 1e6;
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const DEFAULT_WA_EXCHANGE = "مرحباً {customer_name}،\nتفاصيل عملية الصرف رقم {number}:\nاستلمنا: {received_amount} {received_currency}\nسلّمنا: {given_amount} {given_currency}\nسعر الصرف: {rate}\nرابط الإيصال: {receipt_link}\nشكراً لتعاملكم معنا.\n{company_name}\n{office_address}";
const SAMPLE_EX = { customer_name: "سامر العلي", number: 1, received_amount: 200, received_currency: "USD", given_amount: 2724000, given_currency: "SYP", rate: 13620, receipt_link: `${BACKEND}/api/public/exchange-receipt/XXXX` };
// Manual WhatsApp: open wa.me with a pre-filled message the operator sends from their own WhatsApp.
const waSend = (phone, text) => {
  const digits = String(phone || "").replace(/[^\d]/g, "");
  if (!digits) { toast.error("لا يوجد رقم هاتف لهذا العميل"); return; }
  window.open(`https://wa.me/${digits}?text=${encodeURIComponent(text)}`, "_blank");
};
const CurSelect = ({ value, onChange, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-11" data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent className="max-h-72">
      {CUR.map((c) => <SelectItem key={c} value={c}>{currencyName(c)} ({c})</SelectItem>)}
    </SelectContent>
  </Select>
);
const LegSettle = ({ settlement, bankId, banks, onSettle, onBank, idp }) => (
  <div className="space-y-1.5">
    <div className="grid grid-cols-2 gap-2">
      <button type="button" onClick={() => onSettle("cash")} className={`h-8 rounded-md border text-xs font-semibold flex items-center justify-center gap-1.5 transition-all ${settlement === "cash" ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border hover:border-primary/50"}`} data-testid={`${idp}-settle-cash`}><Wallet className="w-3.5 h-3.5" /> الصندوق</button>
      <button type="button" onClick={() => onSettle("bank")} className={`h-8 rounded-md border text-xs font-semibold flex items-center justify-center gap-1.5 transition-all ${settlement === "bank" ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border hover:border-primary/50"}`} data-testid={`${idp}-settle-bank`}><Landmark className="w-3.5 h-3.5" /> بنك</button>
    </div>
    {settlement === "bank" && (
      <Select value={bankId || ""} onValueChange={onBank}>
        <SelectTrigger className="h-9" data-testid={`${idp}-bank-account`}><SelectValue placeholder="اختر الحساب البنكي" /></SelectTrigger>
        <SelectContent>{banks.map((b) => <SelectItem key={b.id} value={b.id}>{b.name} · {b.currency}</SelectItem>)}</SelectContent>
      </Select>
    )}
    {settlement === "bank" && banks.length === 0 && <div className="text-[11px] text-amber-600">لا توجد حسابات بنكية — أضِفها من وحدة البنوك.</div>}
  </div>
);

export default function Exchange() {
  const { user, currentCompany } = useAuth();
  const base = (currentCompany?.base_currency || "SYP").toUpperCase();
  const canDelete = ["super_admin", "company_manager"].includes(user?.role);
  const canManage = ["super_admin", "company_manager"].includes(user?.role);

  const [items, setItems] = useState([]);
  const [banks, setBanks] = useState([]);
  const [summary, setSummary] = useState(null);
  const [settings, setSettings] = useState({ office_address: "", office_whatsapp: "", logo: "", wa_exchange_template: DEFAULT_WA_EXCHANGE });
  const [setDlg, setSetDlg] = useState(false);
  const [sForm, setSForm] = useState({ office_address: "", office_whatsapp: "", logo: "", wa_exchange_template: DEFAULT_WA_EXCHANGE });
  const [savingSet, setSavingSet] = useState(false);
  const [q, setQ] = useState("");
  const [rateMap, setRateMap] = useState({});      // value of 1 X in a common unit (company base or SYP)
  const [rateSrc, setRateSrc] = useState("أسعار الشركة");
  const [manual, setManual] = useState(false);     // true once the user edits the rate/given amount
  const [live, setLive] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    received_currency: "USD", received_amount: "",
    given_currency: base, given_amount: "",
    rate: "", customer_name: "", notes: "",
    customer_phone: "",
    received_settlement: "cash", received_bank_account_id: "",
    given_settlement: "cash", given_bank_account_id: "",
  });

  // how many units of G per 1 unit of R, from a value-map
  const rateOf = (R, G, map) => {
    const vr = +map[R], vg = +map[G];
    return vr > 0 && vg > 0 ? vr / vg : 0;
  };

  const loadTxns = async () => {
    try {
      const [t, b, s, cfg] = await Promise.all([
        api.get("/exchange/transactions", { params: q.trim() ? { q: q.trim() } : {} }),
        api.get("/bank-accounts").catch(() => ({ data: [] })),
        api.get("/exchange/summary").catch(() => ({ data: null })),
        api.get("/exchange/settings").catch(() => ({ data: null })),
      ]);
      setItems(t.data || []); setBanks(b.data || []); setSummary(s.data || null);
      if (cfg.data) setSettings(cfg.data);
    } catch (e) { toast.error(formatErr(e)); }
  };
  const loadCompanyRates = async () => {
    try {
      const { data } = await api.get("/currencies");
      const m = { ...(data.rates || {}) }; m[base] = 1;
      setRateMap(m); setRateSrc("أسعار الشركة");
      return m;
    } catch { return {}; }
  };

  useEffect(() => {
    setManual(false);
    setForm((f) => ({ ...f, given_currency: base, received_amount: "", given_amount: "", rate: "" }));
    loadCompanyRates().then((m) => {
      setForm((f) => {
        const r = rateOf(f.received_currency, base, m);
        return { ...f, given_currency: base, rate: r ? String(round6(r)) : "" };
      });
    });
    // eslint-disable-next-line
  }, [currentCompany?.id]);

  useEffect(() => { loadTxns(); /* eslint-disable-next-line */ }, [currentCompany?.id, q]);

  // recompute given from received & current rate
  const withGiven = (n) => {
    const r = +n.rate;
    if (r > 0 && +n.received_amount > 0) n.given_amount = String(round6(+n.received_amount * r));
    return n;
  };
  // recompute rate from map (auto) unless the user chose manual
  const withAutoRate = (n, map = rateMap) => {
    if (manual) return n;
    const r = rateOf(n.received_currency, n.given_currency, map);
    if (r > 0) n.rate = String(round6(r));
    return withGiven(n);
  };

  const onReceivedAmount = (v) => setForm((f) => withGiven({ ...f, received_amount: v }));
  const onGivenAmount = (v) => setForm((f) => {
    const n = { ...f, given_amount: v };
    if (+f.received_amount > 0 && +v >= 0) { n.rate = String(round6(+v / +f.received_amount)); }
    return n;
  });
  const onGivenAmountCommit = () => setManual(true);
  const onRate = (v) => { setManual(true); setForm((f) => withGiven({ ...f, rate: v })); };
  const onReceivedCur = (v) => setForm((f) => withAutoRate({ ...f, received_currency: v }));
  const onGivenCur = (v) => setForm((f) => withAutoRate({ ...f, given_currency: v }));

  const fetchLive = async () => {
    setFetching(true);
    try {
      const { data } = await api.get("/currencies/live");
      const m = { ...(data.rates || {}) }; m["SYP"] = 1;
      setLive(data); setRateMap(m); setRateSrc(data.source); setManual(false);
      setForm((f) => {
        const r = rateOf(f.received_currency, f.given_currency, m);
        const n = { ...f };
        if (r > 0) n.rate = String(round6(r));
        return withGiven(n);
      });
      toast.success(`تم جلب أسعار ${data.source} — يُحسب المبلغ تلقائياً (يمكن التعديل يدوياً)`);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setFetching(false); }
  };

  const resetAuto = () => {
    setManual(false);
    setForm((f) => {
      const r = rateOf(f.received_currency, f.given_currency, rateMap);
      const n = { ...f };
      if (r > 0) n.rate = String(round6(r));
      return withGiven(n);
    });
    toast.info("عاد السعر إلى الوضع التلقائي");
  };

  const submit = async () => {
    if (!(+form.received_amount > 0) || !(+form.given_amount > 0)) { toast.error("أدخل مبالغ الاستلام والتسليم"); return; }
    if (form.received_currency === form.given_currency) { toast.error("اختر عملتين مختلفتين"); return; }
    if (form.received_settlement === "bank" && !form.received_bank_account_id) { toast.error("اختر الحساب البنكي لِما استلمناه"); return; }
    if (form.given_settlement === "bank" && !form.given_bank_account_id) { toast.error("اختر الحساب البنكي لِما سلّمناه"); return; }
    setSaving(true);
    try {
      await api.post("/exchange/transactions", {
        received_currency: form.received_currency, received_amount: +form.received_amount,
        given_currency: form.given_currency, given_amount: +form.given_amount,
        rate: +form.rate || 0, customer_name: form.customer_name, notes: form.notes,
        customer_phone: form.customer_phone,
        received_settlement: form.received_settlement, received_bank_account_id: form.received_bank_account_id,
        given_settlement: form.given_settlement, given_bank_account_id: form.given_bank_account_id,
      });
      toast.success("تم تسجيل عملية الصرف");
      setForm((f) => ({ ...f, received_amount: "", given_amount: "", customer_name: "", customer_phone: "", notes: "" }));
      loadTxns();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("حذف عملية الصرف وعكس أثرها على الصندوق؟")) return;
    try { await api.delete(`/exchange/transactions/${id}`); toast.success("تم الحذف"); loadTxns(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const openReceipt = async (id) => {
    try {
      const res = await api.get(`/exchange/transactions/${id}/receipt`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const fillTemplate = (tpl, t) => {
    const map = {
      customer_name: t.customer_name || "", number: t.number ?? "",
      received_amount: t.received_amount ?? "", received_currency: t.received_currency || "",
      given_amount: t.given_amount ?? "", given_currency: t.given_currency || "",
      rate: t.rate ?? "", company_name: currentCompany?.name || "", office_address: settings?.office_address || "",
      receipt_link: t.receipt_link || (t.public_token ? `${BACKEND}/api/public/exchange-receipt/${t.public_token}` : ""),
    };
    let out = tpl || "";
    Object.entries(map).forEach(([k, v]) => { out = out.split(`{${k}}`).join(String(v)); });
    return out.replace(/\n{2,}/g, "\n").trim();
  };
  const previewMsg = (tpl) => {
    const map = { ...SAMPLE_EX, company_name: currentCompany?.name || "شركتك", office_address: sForm.office_address || "" };
    let out = tpl || "";
    Object.entries(map).forEach(([k, v]) => { out = out.split(`{${k}}`).join(String(v)); });
    return out.replace(/\n{2,}/g, "\n").trim();
  };
  const onLogo = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 600 * 1024) { toast.error("حجم الشعار كبير — اختر صورة أصغر من 600KB"); return; }
    const reader = new FileReader();
    reader.onload = () => setSForm((s) => ({ ...s, logo: reader.result }));
    reader.readAsDataURL(f);
  };
  const waCustomer = (t) => waSend(t.customer_phone, fillTemplate(settings.wa_exchange_template || DEFAULT_WA_EXCHANGE, t));

  const openSettings = () => { setSForm({ office_address: settings.office_address || "", office_whatsapp: settings.office_whatsapp || "", logo: settings.logo || "", wa_exchange_template: settings.wa_exchange_template || DEFAULT_WA_EXCHANGE }); setSetDlg(true); };
  const saveSettings = async () => {
    setSavingSet(true);
    try {
      const { data } = await api.put("/exchange/settings", sForm);
      setSettings(data); setSetDlg(false); toast.success("تم حفظ إعدادات الصرافة");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSavingSet(false); }
  };

  return (
    <div className="space-y-6" dir="rtl" data-testid="exchange-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold flex items-center gap-2">
            <ArrowLeftRight className="w-7 h-7 text-primary" /> الصرافة — بيع وشراء العملات
          </h1>
          <p className="text-sm text-muted-foreground mt-1">سجّل عمليات الصرف؛ تنعكس تلقائياً على صناديق العملات والقيود المحاسبية.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="gap-2 h-10" onClick={fetchLive} disabled={fetching} data-testid="exchange-fetch-live">
            <RefreshCw className={`w-4 h-4 ${fetching ? "animate-spin" : ""}`} /> أسعار الليرة اليوم
          </Button>
          {canManage && (
            <Button variant="outline" className="gap-2 h-10" onClick={openSettings} data-testid="exchange-settings-btn">
              <Settings className="w-4 h-4" /> قالب واتساب
            </Button>
          )}
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="exchange-summary">
          <Card className="p-4 bg-primary/5 border-primary/20">
            <div className="text-xs font-semibold text-primary mb-1">ربح الصرافة اليوم</div>
            <div className="text-xl font-bold tabular-nums" data-testid="summary-profit-today">{fmtMoney(summary.profit_today || 0, summary.base_currency)}</div>
            <div className="text-[11px] text-muted-foreground mt-1">عمليات اليوم: {summary.exch_count_today || 0}</div>
          </Card>
          {(summary.positions || []).slice(0, 3).map((p) => (
            <Card className="p-4" key={p.currency} data-testid={`position-${p.currency}`}>
              <div className="text-xs font-semibold text-muted-foreground mb-1">مركز {p.currency}</div>
              <div className="text-lg font-bold tabular-nums">{p.total.toLocaleString("ar-EG")}</div>
              <div className="text-[11px] text-muted-foreground mt-1">صندوق {p.cash.toLocaleString("ar-EG")} · بنك {p.bank.toLocaleString("ar-EG")}</div>
            </Card>
          ))}
        </div>
      )}

      {live && (
        <Card className="p-4" data-testid="exchange-live-card">
          <div className="flex items-center gap-2 text-xs font-semibold text-primary mb-2">
            <TrendingUp className="w-4 h-4" /> {live.source} · {new Date(live.fetched_at).toLocaleString("ar-EG")} · مقابل الليرة السورية
          </div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(live.rates || {}).filter(([k]) => k !== "SYP").map(([k, v]) => (
              <span key={k} className="text-xs px-2.5 py-1 rounded-md border bg-muted/40 tabular-nums" data-testid={`live-rate-${k}`}>
                <b>{k}</b> = {(+v).toLocaleString("ar-EG")} ل.س
              </span>
            ))}
          </div>
        </Card>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-6 space-y-4" data-testid="exchange-form">
          <div className="flex items-center gap-2"><Coins className="w-4 h-4 text-primary" /><h2 className="font-heading font-bold">عملية صرف جديدة</h2></div>

          <div>
            <Label>اسم العميل (اختياري)</Label>
            <Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} placeholder="اسم الزبون" data-testid="exchange-customer" className="h-11" />
          </div>

          <div>
            <Label>هاتف العميل (واتساب — اختياري)</Label>
            <Input value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} placeholder="مع رمز الدولة، مثال 9639xxxxxxxx" data-testid="exchange-customer-phone" className="h-11 tabular-nums" dir="ltr" />
          </div>

          <div className="rounded-md border border-emerald-200 bg-emerald-50/60 p-3 space-y-2">
            <div className="text-xs font-semibold text-emerald-700">استلمنا من العميل</div>
            <div className="grid grid-cols-[1fr,150px] gap-2">
              <Input type="number" step="any" min="0" value={form.received_amount} onChange={(e) => onReceivedAmount(e.target.value)} placeholder="المبلغ" className="h-11 tabular-nums text-lg font-bold" data-testid="exchange-received-amount" />
              <CurSelect value={form.received_currency} onChange={onReceivedCur} testid="exchange-received-currency" />
            </div>
            <LegSettle settlement={form.received_settlement} bankId={form.received_bank_account_id} banks={banks}
              onSettle={(v) => setForm((f) => ({ ...f, received_settlement: v, received_bank_account_id: v === "cash" ? "" : f.received_bank_account_id }))}
              onBank={(v) => setForm((f) => ({ ...f, received_bank_account_id: v }))} idp="exchange-received" />
          </div>

          {/* Rate — auto-filled from fetched rates, editable manually */}
          <div className="rounded-md border border-primary/20 bg-primary/5 p-3 space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-xs">سعر الصرف ({form.received_currency} ← {form.given_currency})</Label>
              <span className={`text-[10px] px-2 py-0.5 rounded ${manual ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`} data-testid="exchange-rate-source">
                {manual ? "يدوي" : rateSrc}
              </span>
            </div>
            <div className="grid grid-cols-[1fr,auto] gap-2">
              <Input type="number" step="any" min="0" value={form.rate} onChange={(e) => onRate(e.target.value)} placeholder="سيُملأ تلقائياً عند جلب الأسعار" className="h-10 tabular-nums font-bold" data-testid="exchange-rate-input" />
              {manual && (
                <Button variant="outline" size="sm" className="h-10 gap-1.5" onClick={resetAuto} data-testid="exchange-rate-reset"><RotateCcw className="w-3.5 h-3.5" /> تلقائي</Button>
              )}
            </div>
            <div className="text-[11px] text-muted-foreground">1 {form.given_currency} = {form.rate ? (+form.rate).toLocaleString("ar-EG", { maximumFractionDigits: 6 }) : "؟"} {form.received_currency}</div>
          </div>

          <div className="rounded-md border border-amber-200 bg-amber-50/60 p-3 space-y-2">
            <div className="text-xs font-semibold text-amber-700">سلّمنا للعميل (يُحسب تلقائياً)</div>
            <div className="grid grid-cols-[1fr,150px] gap-2">
              <Input type="number" step="any" min="0" value={form.given_amount} onChange={(e) => onGivenAmount(e.target.value)} onBlur={onGivenAmountCommit} placeholder="المبلغ" className="h-11 tabular-nums text-lg font-bold" data-testid="exchange-given-amount" />
              <CurSelect value={form.given_currency} onChange={onGivenCur} testid="exchange-given-currency" />
            </div>
            <LegSettle settlement={form.given_settlement} bankId={form.given_bank_account_id} banks={banks}
              onSettle={(v) => setForm((f) => ({ ...f, given_settlement: v, given_bank_account_id: v === "cash" ? "" : f.given_bank_account_id }))}
              onBank={(v) => setForm((f) => ({ ...f, given_bank_account_id: v }))} idp="exchange-given" />
          </div>

          <div>
            <Label>ملاحظات (اختياري)</Label>
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="exchange-notes" className="h-11" />
          </div>

          <Button onClick={submit} disabled={saving} className="w-full h-11" data-testid="exchange-submit">
            <Save className="w-4 h-4 me-2" /> {saving ? "جارٍ الحفظ..." : "تسجيل العملية"}
          </Button>
        </Card>

        <Card className="overflow-hidden" data-testid="exchange-list">
          <div className="p-3 border-b border-border flex items-center gap-2">
            <span className="font-heading font-bold flex-1">آخر العمليات</span>
            <div className="relative w-48">
              <Search className="w-4 h-4 absolute top-2.5 start-2.5 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="بحث بالعميل" className="h-9 ps-8" data-testid="exchange-search" />
            </div>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-start">#</TableHead>
                  <TableHead className="text-start">استلمنا</TableHead>
                  <TableHead className="text-start">سلّمنا</TableHead>
                  <TableHead className="text-center">الربح ({base})</TableHead>
                  <TableHead className="text-center">إجراء</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((t) => (
                  <TableRow key={t.id} data-testid={`exchange-row-${t.id}`}>
                    <TableCell className="text-start font-mono text-xs">{t.number}</TableCell>
                    <TableCell className="text-start tabular-nums text-emerald-700">{fmtMoney(t.received_amount, t.received_currency)}</TableCell>
                    <TableCell className="text-start tabular-nums text-amber-700">{fmtMoney(t.given_amount, t.given_currency)}</TableCell>
                    <TableCell className={`text-center tabular-nums font-bold ${(t.profit_base || 0) < 0 ? "text-destructive" : "text-primary"}`}>{fmtMoney(t.profit_base || 0, base)}</TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        {t.customer_phone && <Button size="icon" variant="ghost" className="h-8 w-8 text-emerald-600" title="إرسال عبر واتساب" data-testid={`exchange-whatsapp-${t.id}`}
                          onClick={() => waCustomer(t)}>
                          <MessageCircle className="w-4 h-4" /></Button>}
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-primary" onClick={() => openReceipt(t.id)} data-testid={`exchange-receipt-${t.id}`} title="إيصال PDF"><FileText className="w-4 h-4" /></Button>
                        {canDelete && <Button size="icon" variant="ghost" className="text-destructive h-8 w-8" onClick={() => del(t.id)} data-testid={`exchange-delete-${t.id}`}><Trash2 className="w-4 h-4" /></Button>}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {items.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">لا توجد عمليات بعد.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </Card>
      </div>

      {/* WhatsApp template settings */}
      <Dialog open={setDlg} onOpenChange={setSetDlg}>
        <DialogContent dir="rtl" className="max-w-lg" data-testid="exchange-settings-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Settings className="w-5 h-5 text-primary" /> إعدادات الصرافة وواتساب</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">عنوان المكتب / معلومات التواصل</Label>
              <Input value={sForm.office_address} onChange={(e) => setSForm((s) => ({ ...s, office_address: e.target.value }))} placeholder="مثال: دمشق - شارع بغداد - هاتف 011xxxxxxx" className="h-10" data-testid="exchange-settings-office-address" />
            </div>
            <div>
              <Label className="text-xs">رقم واتساب المكتب (يظهر كرمز QR على الإيصال)</Label>
              <Input value={sForm.office_whatsapp} onChange={(e) => setSForm((s) => ({ ...s, office_whatsapp: e.target.value }))} placeholder="مع رمز الدولة، مثال 9639xxxxxxxx" className="h-10 tabular-nums" dir="ltr" data-testid="exchange-settings-office-whatsapp" />
            </div>
            <div>
              <Label className="text-xs">شعار الشركة (يظهر أعلى الإيصال)</Label>
              <div className="flex items-center gap-3 mt-1">
                {sForm.logo
                  ? <img src={sForm.logo} alt="logo" className="h-12 w-auto max-w-[120px] object-contain rounded border border-border bg-white p-1" data-testid="exchange-settings-logo-preview" />
                  : <div className="h-12 w-20 rounded border border-dashed border-border flex items-center justify-center text-[10px] text-muted-foreground">لا شعار</div>}
                <input type="file" accept="image/png,image/jpeg,image/webp" onChange={onLogo} className="text-xs" data-testid="exchange-settings-logo-input" />
                {sForm.logo && <Button variant="ghost" size="sm" className="text-destructive h-8" onClick={() => setSForm((s) => ({ ...s, logo: "" }))} data-testid="exchange-settings-logo-remove">إزالة</Button>}
              </div>
            </div>
            <div>
              <Label className="text-xs">قالب رسالة واتساب لعميل الصرافة</Label>
              <textarea value={sForm.wa_exchange_template} onChange={(e) => setSForm((s) => ({ ...s, wa_exchange_template: e.target.value }))} rows={5}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-y focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="exchange-settings-template" />
              <div className="mt-1 rounded-md bg-emerald-50 border border-emerald-100 p-2 text-[11px] text-emerald-900 whitespace-pre-wrap leading-relaxed" data-testid="exchange-settings-preview">{previewMsg(sForm.wa_exchange_template)}</div>
            </div>
            <div className="text-[11px] text-muted-foreground bg-muted/50 rounded-md p-2 leading-relaxed">
              الحقول المتاحة: <code dir="ltr">{"{customer_name} {number} {received_amount} {received_currency} {given_amount} {given_currency} {rate} {receipt_link} {company_name} {office_address}"}</code>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSForm((s) => ({ ...s, wa_exchange_template: DEFAULT_WA_EXCHANGE }))} data-testid="exchange-settings-reset">استعادة الافتراضي</Button>
            <Button onClick={saveSettings} disabled={savingSet} data-testid="exchange-settings-save"><Save className="w-4 h-4 me-2" /> {savingSet ? "جارٍ الحفظ..." : "حفظ"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
